import React, { useState, useEffect, useRef } from 'react';
import {
  Monitor, Wifi, WifiOff, Clock, AppWindow, Globe, Users
} from 'lucide-react';
import adminSocketService from '../services/socketService';
import './MainScreen.css';
import cookies from 'js-cookie';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';
import { API_BASE_URL } from '../config';

// --- Custom Hooks ---
function useCounter(end, duration = 1000) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTimestamp = null;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const easeOutQuad = 1 - (1 - progress) * (1 - progress);
      setCount(Math.floor(easeOutQuad * end));
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    window.requestAnimationFrame(step);
  }, [end, duration]);

  return count;
}

// --- Components ---
function PremiumStatCard({ title, value, suffix, icon, color, statusDot, subText, delay }) {
  const count = useCounter(value, 1500);
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const r = cardRef.current.getBoundingClientRect();
    const cx = (e.clientX - r.left - r.width / 2) / r.width;
    const cy = (e.clientY - r.top - r.height / 2) / r.height;
    setTilt({ x: cy * 15, y: -cx * 15 });
  };

  const handleMouseLeave = () => setTilt({ x: 0, y: 0 });

  return (
    <div
      ref={cardRef}
      className="dash-glass-card stat-card"
      style={{
        animationDelay: delay,
        transform: `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) scale3d(1, 1, 1)`,
        borderColor: tilt.x !== 0 ? 'rgba(var(--app-primary-color-rgb), 0.5)' : 'var(--border-light)',
        overflow: 'hidden'
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(var(--app-primary-color-rgb), 0.1)', border: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>
            {icon}
          </div>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>{title}</span>
        </div>
        {statusDot && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--input-bg)', padding: '4px 8px', borderRadius: 12 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusDot, boxShadow: `0 0 8px ${statusDot}` }} className={statusDot === '#22C55E' ? 'anim-blink' : ''} />
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)' }}>{statusDot === '#22C55E' ? 'LIVE' : statusDot === '#EF4444' ? 'DOWN' : 'IDLE'}</span>
          </div>
        )}
      </div>

      <div style={{ fontSize: 36, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.03em', lineHeight: 1, marginBottom: 8 }}>
        {count}{suffix}
      </div>

      {subText && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
          {subText}
        </div>
      )}

      {title === 'Total Computers' && (
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: 3, background: 'var(--accent-gradient)', borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }} />
      )}
    </div>
  );
}

function TopAppsTable() {
  const [activeTab, setActiveTab] = useState('apps');
  const [topCount, setTopCount] = useState(5);
  const [apiData, setApiData] = useState({ websites: [], apps: [] });

  useEffect(() => {
    const fetchUsage = async () => {
      try {
        const adminId = cookies.get('adminId');
        const response = await fetch(`${API_BASE_URL}/app-usage?adminId=${adminId}`);
        if (!response.ok) throw new Error('Failed to fetch');
        const data = await response.json();

        const formatTime = (ms) => {
          const hours = Math.floor(ms / (1000 * 60 * 60));
          const mins = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
          if (hours > 0) return `${hours}h ${mins}m`;
          return `${mins}m`;
        };

        // Process Applications
        const appMap = {};
        data.applications.forEach(log => {
          const name = log.appName || log.windowTitle || 'Unknown';
          if (!appMap[name]) appMap[name] = { timeMs: 0, users: new Set() };

          const start = new Date(log.startTime).getTime();
          const end = log.endTime ? new Date(log.endTime).getTime() : new Date(log.updatedAt).getTime();
          appMap[name].timeMs += Math.max(0, end - start);
          appMap[name].users.add(log.computerName);
        });

        const apps = Object.keys(appMap).map(name => ({
          name,
          timeMs: appMap[name].timeMs,
          time: formatTime(appMap[name].timeMs),
          users: appMap[name].users.size
        })).sort((a, b) => b.timeMs - a.timeMs).map((item, idx) => ({ ...item, rank: idx + 1 }));

        // Process Websites
        const webMap = {};
        data.websites.forEach(log => {
          if (!log.url || typeof log.url !== 'string' || log.url.trim() === '') return;

          let urlName = log.url;
          try {
            const urlObj = new URL(log.url);
            urlName = urlObj.host || log.url;
          } catch (e) {
            if (urlName.startsWith('http')) urlName = urlName.split('/')[2];
          }

          if (!urlName || urlName.trim() === '') return;

          if (!webMap[urlName]) webMap[urlName] = { timeMs: 0, users: new Set() };

          const start = new Date(log.startTime).getTime();
          const end = log.endTime ? new Date(log.endTime).getTime() : new Date(log.updatedAt).getTime();
          webMap[urlName].timeMs += Math.max(0, end - start);
          webMap[urlName].users.add(log.computerName);
        });

        const websites = Object.keys(webMap).map(name => ({
          name,
          timeMs: webMap[name].timeMs,
          time: formatTime(webMap[name].timeMs),
          users: webMap[name].users.size
        })).sort((a, b) => b.timeMs - a.timeMs).map((item, idx) => ({ ...item, rank: idx + 1 }));

        setApiData({ apps, websites });
      } catch (err) {
        console.error('Error fetching app usage:', err);
      }
    };
    fetchUsage();
    const interval = setInterval(fetchUsage, 60000);
    return () => clearInterval(interval);
  }, []);

  const data = activeTab === 'apps' ? apiData.apps : apiData.websites;
  const items = data.slice(0, topCount);

  return (
    <div className="dash-glass-card animate-fade-in" style={{ animationDelay: '700ms', padding: '24px 0 0', gridColumn: '1 / -1', overflow: 'hidden' }}>
      <div className="section-header-wrap top-apps-px">
        <div className="section-tabs-wrap">
          <button
            className="nm-link"
            style={{ fontSize: 16, fontWeight: activeTab === 'apps' ? 800 : 500, color: activeTab === 'apps' ? 'var(--text-primary)' : 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
            onClick={() => setActiveTab('apps')}
          >
            Applications
          </button>
          <button
            className="nm-link"
            style={{ fontSize: 16, fontWeight: activeTab === 'websites' ? 800 : 500, color: activeTab === 'websites' ? 'var(--text-primary)' : 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
            onClick={() => setActiveTab('websites')}
          >
            Websites
          </button>
        </div>

        <div className="segment-control" style={{ display: 'flex', gap: 8, background: 'var(--input-bg)', padding: 4, borderRadius: 8 }}>
          {[5, 10, 20].map(opt => (
            <button
              key={opt}
              style={{
                background: topCount === opt ? 'rgba(var(--app-primary-color-rgb), 0.15)' : 'transparent',
                border: 'none',
                color: topCount === opt ? 'var(--accent-primary)' : 'var(--text-secondary)',
                padding: '6px 12px',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600
              }}
              onClick={() => setTopCount(opt)}
            >
              Top {opt}
            </button>
          ))}
        </div>
      </div>

      {/* Horizontal Bar Chart for Usage Time */}
      <div className="top-apps-px" style={{ marginBottom: 32, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Usage Time Chart</div>
        {items.map((item) => {
          const maxValMs = Math.max(...items.map(i => i.timeMs), 1);
          const pct = Math.min((item.timeMs / maxValMs) * 100, 100);
          return (
            <div key={`chart-${item.name}`} className="usage-chart-row">
              <div className="usage-chart-label" title={item.name}>
                <span className="full-name">{item.name}</span>
                <span className="short-name">{item.name.substring(0, 3)}...</span>
              </div>
              <div style={{ flex: 1, height: 6, background: 'var(--input-bg)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{
                  width: `${pct}%`,
                  height: '100%',
                  background: 'var(--accent-gradient)',
                  borderRadius: 3,
                  animation: 'scaleX 1s cubic-bezier(0.16, 1, 0.3, 1)',
                  transformOrigin: 'left'
                }} />
              </div>
              <div className="usage-chart-value">{item.time}</div>
            </div>
          );
        })}
      </div>

      <div className="dash-table-wrapper" style={{ overflowX: 'auto', padding: 0 }}>
        <table className="dash-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
              <th style={{ backgroundColor: 'var(--bg-card)', padding: '12px 16px 12px 24px', color: 'var(--text-muted)', fontWeight: 600, fontSize: 12 }}>Rank</th>
              <th style={{ backgroundColor: 'var(--bg-card)', padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600, fontSize: 12 }}>App / Website</th>
              <th style={{ backgroundColor: 'var(--bg-card)', padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600, fontSize: 12 }}>Usage Time</th>
              <th style={{ backgroundColor: 'var(--bg-card)', padding: '12px 24px 12px 16px', color: 'var(--text-muted)', fontWeight: 600, fontSize: 12 }}>Active Users</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.name} style={{ borderBottom: '1px solid var(--border-light)' }}>
                <td style={{ padding: '12px 16px 12px 24px', fontWeight: 800, color: 'var(--text-muted)' }}>#{item.rank}</td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div className="app-icon" style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(var(--app-primary-color-rgb), 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {activeTab === 'apps' ? <AppWindow size={14} color="var(--accent-primary)" /> : <Globe size={14} color="var(--accent-primary)" />}
                    </div>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}>{item.name}</span>
                  </div>
                </td>
                <td style={{ padding: '12px 16px', color: 'var(--text-primary)', fontSize: 13 }}>{item.time}</td>
                <td style={{ padding: '12px 24px 12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)', fontSize: 13 }}>
                    <Users size={12} color="var(--text-muted)" /> {item.users}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CustomDropdown({ options, value, onChange }) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        style={{
          background: 'var(--input-bg)',
          border: '1px solid var(--border-light)',
          color: 'var(--text-secondary)',
          padding: '6px 12px',
          borderRadius: 8,
          fontSize: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          cursor: 'pointer',
          transition: 'all 200ms'
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(var(--app-primary-color-rgb), 0.08)'}
        onMouseLeave={e => e.currentTarget.style.background = 'var(--input-bg)'}
      >
        {value}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }}><polyline points="6 9 12 15 18 9"></polyline></svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          marginTop: 4,
          background: 'var(--bg-card)',
          border: '1px solid var(--border-light)',
          borderRadius: 8,
          padding: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          zIndex: 50,
          minWidth: 140,
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
          animation: 'slideUpFade 200ms both'
        }}>
          {options.map(opt => (
            <button
              key={opt}
              onClick={() => { onChange(opt); setOpen(false) }}
              style={{
                background: value === opt ? 'rgba(var(--app-primary-color-rgb), 0.12)' : 'transparent',
                color: value === opt ? 'var(--accent-primary)' : 'var(--text-secondary)',
                padding: '8px 12px',
                borderRadius: 4,
                textAlign: 'left',
                fontSize: 12,
                fontWeight: value === opt ? 600 : 400,
                cursor: 'pointer',
                border: 'none',
                transition: 'background 200ms'
              }}
              onMouseEnter={e => { if (value !== opt) e.currentTarget.style.background = 'rgba(var(--app-primary-color-rgb), 0.06)' }}
              onMouseLeave={e => { if (value !== opt) e.currentTarget.style.background = 'transparent' }}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AllIdlePCsChart() {
  const [filter, setFilter] = useState('Today');
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchAllIdle = async () => {
      try {
        setLoading(true);
        const adminId = cookies.get('adminId') || localStorage.getItem('adminId');
        if (!adminId) return;

        const response = await fetch(`${API_BASE_URL}/admins/${adminId}/all-idle-pcs?filter=${encodeURIComponent(filter)}`);
        if (response.ok) {
          const data = await response.json();
          const formattedData = data.map(pc => ({
            name: pc.assignedUser ? `${pc.computerName} (${pc.assignedUser})` : pc.computerName,
            idleMs: pc.totalIdleMs,
            idleHours: Number((pc.totalIdleMs / (1000 * 60 * 60)).toFixed(2))
          }));
          setChartData(formattedData);
        }
      } catch (err) {
        console.error('Failed to fetch All Idle PCs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAllIdle();
  }, [filter]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const ms = payload[0].payload.idleMs;
      const hours = Math.floor(ms / (1000 * 60 * 60));
      const mins = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
      return (
        <div style={{ background: 'var(--bg-card)', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border-light)' }}>
          <p style={{ color: 'var(--text-primary)', margin: '0 0 4px 0', fontWeight: 600, fontSize: 13 }}>{label}</p>
          <p style={{ color: 'var(--accent-primary)', margin: 0, fontSize: 12 }}>{`Idle Time: ${hours}h ${mins}m`}</p>
        </div>
      );
    }
    return null;
  };

  const totalAllIdleMs = chartData.reduce((acc, curr) => acc + (curr.idleMs || 0), 0);
  const tHours = Math.floor(totalAllIdleMs / (1000 * 60 * 60));
  const tMins = Math.floor((totalAllIdleMs % (1000 * 60 * 60)) / (1000 * 60));

  return (
    <div className="dash-glass-card animate-fade-in" style={{ animationDelay: '800ms', padding: 24, display: 'flex', flexDirection: 'column' }}>
      <div className="section-header-wrap">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-primary)' }}>Total Idle PCs Chart</h2>
        </div>

        <CustomDropdown
          value={filter}
          onChange={setFilter}
          options={['Today', 'Yesterday', 'Last 7 Days', 'Up to this month']}
        />
      </div>

      <div style={{ flex: 1, minHeight: 250, marginTop: 16 }}>
        {loading ? (
          <div style={{ color: 'var(--text-secondary)', fontSize: 13, textAlign: 'center', padding: '40px 0' }}>Loading chart...</div>
        ) : chartData.length === 0 ? (
          <div style={{ color: 'var(--text-secondary)', fontSize: 13, textAlign: 'center', padding: '40px 0' }}>No idle PCs found.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" vertical={false} />
              <XAxis dataKey="name" stroke="var(--text-muted)" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="var(--text-muted)" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(val) => `${val}h`} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(var(--app-primary-color-rgb), 0.05)' }} />
              <Bar dataKey="idleHours" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.idleHours > 4 ? '#EF4444' : entry.idleHours > 2 ? '#EAB308' : 'var(--accent-primary)'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div style={{ marginTop: 24, padding: '14px 16px', background: 'rgba(var(--app-primary-color-rgb), 0.08)', border: '1px solid rgba(var(--app-primary-color-rgb), 0.2)', borderRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Total Idle (All PCs)</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--accent-primary)' }}>{tHours > 0 ? `${tHours}h ${tMins}m` : `${tMins}m`}</div>
        </div>
      </div>
    </div>
  );
}

function TopIdlePCs() {
  const [filter, setFilter] = useState('Today');
  const [apiData, setApiData] = useState({ topPCs: [], totalInefficiency: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchIdlePCs = async () => {
      try {
        setLoading(true);
        const adminId = cookies.get('adminId') || localStorage.getItem('adminId');
        if (!adminId) return;

        const response = await fetch(`${API_BASE_URL}/admins/${adminId}/top-idle-pcs?filter=${encodeURIComponent(filter)}`);
        if (response.ok) {
          const data = await response.json();
          setApiData(data);
        }
      } catch (err) {
        console.error('Failed to fetch Top Idle PCs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchIdlePCs();
  }, [filter]);

  const formatTime = (ms) => {
    if (!ms) return '0m';
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const mins = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const getStatusColor = (ms) => {
    const hours = ms / (1000 * 60 * 60);
    if (hours > 4) return '#EF4444';
    if (hours > 2) return '#EAB308';
    return 'var(--accent-primary)';
  };

  const idlePCs = apiData.topPCs || [];
  const topPCsTotalMs = idlePCs.reduce((acc, pc) => acc + (pc.totalIdleMs || 0), 0);
  const currentInefficiency = formatTime(topPCsTotalMs);

  return (
    <div className="dash-glass-card animate-fade-in" style={{ animationDelay: '900ms', padding: 24, display: 'flex', flexDirection: 'column' }}>
      <div className="section-header-wrap">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-primary)' }}>Top 5 Idle PCs</h2>
        </div>

        <CustomDropdown
          value={filter}
          onChange={setFilter}
          options={['Today', 'Yesterday', 'Last 7 Days', 'Up to this month']}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1, marginTop: 16 }}>
        {idlePCs.length === 0 && !loading && (
          <div style={{ color: 'var(--text-secondary)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No idle PCs found for this period.</div>
        )}
        {loading && (
          <div style={{ color: 'var(--text-secondary)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>Loading...</div>
        )}
        {!loading && idlePCs.map((pc) => (
          <div key={pc._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: 'var(--input-bg)', borderRadius: 12, border: '1px solid var(--border-light)', transition: 'all 300ms', cursor: 'default' }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(var(--app-primary-color-rgb), 0.08)'} onMouseLeave={e => e.currentTarget.style.background = 'var(--input-bg)'}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(var(--app-primary-color-rgb), 0.15)', color: getStatusColor(pc.totalIdleMs), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontWeight: 700, fontSize: 13 }}>
              {(pc.assignedUser || pc.employeeId || pc.computerName).substring(0, 2).toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{pc.computerName}</span>
                <span style={{ fontSize: 12, color: getStatusColor(pc.totalIdleMs), fontWeight: 700 }}>{formatTime(pc.totalIdleMs)}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between' }}>
                <span>{pc.assignedUser || pc.employeeId || 'Unknown'}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 24, padding: '14px 16px', background: 'rgba(var(--app-primary-color-rgb), 0.08)', border: '1px solid rgba(var(--app-primary-color-rgb), 0.2)', borderRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>System Inefficiency</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--accent-primary)' }}>{currentInefficiency}</div>
        </div>
      </div>
    </div>
  );
}

const MainScreen = () => {
  const [stats, setStats] = useState({ total: 0, online: 0, offline: 0, idle: 0 });

  useEffect(() => {
    const socket = adminSocketService.connect();

    const handleDeviceList = (realDevices) => {
      const total = realDevices.length;
      let online = 0;
      let offline = 0;
      let idle = 0;

      realDevices.forEach(rd => {
        const status = rd.isOnline === false ? 'OFFLINE' : ((rd.idleTime && rd.idleTime >= 20) ? 'IDLE' : 'ONLINE');
        if (status === 'OFFLINE') offline++;
        else if (status === 'IDLE') idle++;
        else online++;
      });

      setStats({ total, online, offline, idle });
    };

    socket.on('device-list', handleDeviceList);

    return () => {
      socket.off('device-list', handleDeviceList);
    };
  }, []);

  return (
    <main className="main-screen">
      <div className="dash-grid-container">
        <div className="stats-grid animate-fade-in" style={{ animationDelay: '100ms' }}>
          <PremiumStatCard
            title="Total Computers"
            value={stats.total}
            icon={<Monitor size={18} />}
            color="var(--accent-primary)"
            delay="100ms"
          />
          <PremiumStatCard
            title="Online PCs"
            value={stats.online}
            icon={<Wifi size={18} />}
            color="#22C55E"
            statusDot="#22C55E"
            delay="200ms"
          />
          <PremiumStatCard
            title="Offline PCs"
            value={stats.offline}
            icon={<WifiOff size={18} />}
            color="#EF4444"
            statusDot="#EF4444"
            delay="300ms"
          />
          <PremiumStatCard
            title="Idle PCs"
            value={stats.idle}
            icon={<Clock size={18} />}
            color="#EAB308"
            statusDot="#EAB308"
            delay="400ms"
          />
        </div>

        <TopAppsTable />

        <div className="dash-grid-main">
          <AllIdlePCsChart />
          <TopIdlePCs />
        </div>
      </div>
    </main>
  );
};

export default MainScreen;
